/**
 * Offline Voice Cache
 * Queues voice recordings when offline and transcribes when connection returns
 */
import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { WifiOff, Wifi, CloudOff, Upload, Check, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// IndexedDB configuration
const DB_NAME = 'viralla_voice_cache';
const DB_VERSION = 1;
const STORE_NAME = 'recordings';

// Open IndexedDB
const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        store.createIndex('timestamp', 'timestamp');
        store.createIndex('status', 'status');
      }
    };
  });
};

// Voice Cache Context
const VoiceCacheContext = createContext(null);

export const useVoiceCache = () => {
  const context = useContext(VoiceCacheContext);
  if (!context) {
    throw new Error('useVoiceCache must be used within a VoiceCacheProvider');
  }
  return context;
};

export const VoiceCacheProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  // Monitor online status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('Back online! Processing queued recordings...');
      processQueue();
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('You\'re offline. Recordings will be saved locally.');
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load pending count on mount
  useEffect(() => {
    loadPendingCount();
  }, []);

  const loadPendingCount = async () => {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const index = store.index('status');
      const request = index.count(IDBKeyRange.only('pending'));
      
      request.onsuccess = () => {
        setPendingCount(request.result);
      };
    } catch (error) {
      console.error('Failed to load pending count:', error);
    }
  };

  // Save recording to cache
  const saveRecording = useCallback(async (audioBlob, metadata = {}) => {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      
      // Convert blob to array buffer for storage
      const arrayBuffer = await audioBlob.arrayBuffer();
      
      const record = {
        audio: arrayBuffer,
        mimeType: audioBlob.type,
        timestamp: Date.now(),
        status: 'pending',
        metadata,
        retryCount: 0
      };
      
      await new Promise((resolve, reject) => {
        const request = store.add(record);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
      
      await loadPendingCount();
      
      if (isOnline) {
        processQueue();
      } else {
        toast.info('Recording saved offline. Will transcribe when online.');
      }
      
      return true;
    } catch (error) {
      console.error('Failed to save recording:', error);
      toast.error('Failed to save recording');
      return false;
    }
  }, [isOnline]);

  // Process pending recordings
  const processQueue = useCallback(async () => {
    if (isProcessing || !isOnline) return;
    
    setIsProcessing(true);
    
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const index = store.index('status');
      
      const pendingRecords = await new Promise((resolve, reject) => {
        const request = index.getAll(IDBKeyRange.only('pending'));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
      
      if (pendingRecords.length === 0) {
        setIsProcessing(false);
        return;
      }
      
      setProgress({ current: 0, total: pendingRecords.length });
      
      for (let i = 0; i < pendingRecords.length; i++) {
        const record = pendingRecords[i];
        setProgress({ current: i + 1, total: pendingRecords.length });
        
        try {
          // Convert array buffer back to blob
          const audioBlob = new Blob([record.audio], { type: record.mimeType });
          
          // Send to backend
          const formData = new FormData();
          formData.append('audio', audioBlob, 'recording.webm');
          formData.append('language', record.metadata.language || 'en');
          
          const token = localStorage.getItem('authToken');
          const response = await axios.post(`${API}/api/voice/transcribe`, formData, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            },
            timeout: 60000 // 1 minute timeout
          });
          
          // Update record status
          const updateTx = db.transaction(STORE_NAME, 'readwrite');
          const updateStore = updateTx.objectStore(STORE_NAME);
          
          record.status = 'completed';
          record.transcript = response.data.transcript;
          record.completedAt = Date.now();
          
          await new Promise((resolve, reject) => {
            const request = updateStore.put(record);
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
          });
          
          // Call callback if provided
          if (record.metadata.onTranscript) {
            record.metadata.onTranscript(response.data.transcript);
          }
          
        } catch (error) {
          console.error('Failed to process recording:', error);
          
          // Update retry count
          const updateTx = db.transaction(STORE_NAME, 'readwrite');
          const updateStore = updateTx.objectStore(STORE_NAME);
          
          record.retryCount = (record.retryCount || 0) + 1;
          if (record.retryCount >= 3) {
            record.status = 'failed';
            record.error = error.message;
          }
          
          await new Promise((resolve, reject) => {
            const request = updateStore.put(record);
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
          });
        }
      }
      
      await loadPendingCount();
      
      const completed = pendingRecords.filter(r => r.status === 'completed').length;
      if (completed > 0) {
        toast.success(`Transcribed ${completed} queued recording${completed > 1 ? 's' : ''}`);
      }
      
    } catch (error) {
      console.error('Queue processing error:', error);
    } finally {
      setIsProcessing(false);
      setProgress({ current: 0, total: 0 });
    }
  }, [isOnline, isProcessing]);

  // Clear completed recordings
  const clearCompleted = useCallback(async () => {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const index = store.index('status');
      
      const completed = await new Promise((resolve, reject) => {
        const request = index.getAllKeys(IDBKeyRange.only('completed'));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
      
      for (const key of completed) {
        store.delete(key);
      }
      
      toast.success(`Cleared ${completed.length} completed recordings`);
    } catch (error) {
      console.error('Failed to clear completed:', error);
    }
  }, []);

  // Get all recordings
  const getRecordings = useCallback(async () => {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      
      return new Promise((resolve, reject) => {
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Failed to get recordings:', error);
      return [];
    }
  }, []);

  const value = {
    isOnline,
    pendingCount,
    isProcessing,
    progress,
    saveRecording,
    processQueue,
    clearCompleted,
    getRecordings
  };

  return (
    <VoiceCacheContext.Provider value={value}>
      {children}
    </VoiceCacheContext.Provider>
  );
};

/**
 * Offline status indicator component
 */
export const OfflineIndicator = ({ className = '' }) => {
  const { isOnline, pendingCount, isProcessing, progress, processQueue } = useVoiceCache();

  if (isOnline && pendingCount === 0 && !isProcessing) {
    return null; // Don't show when online with no pending items
  }

  return (
    <div 
      className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
        isOnline 
          ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300' 
          : 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300'
      } ${className}`}
      role="status"
      aria-live="polite"
    >
      {isOnline ? (
        <Wifi className="w-4 h-4" aria-hidden="true" />
      ) : (
        <WifiOff className="w-4 h-4" aria-hidden="true" />
      )}
      
      <span className="text-sm font-medium">
        {isProcessing ? (
          <>Processing {progress.current}/{progress.total}...</>
        ) : !isOnline ? (
          'Offline Mode'
        ) : pendingCount > 0 ? (
          `${pendingCount} pending`
        ) : (
          'Online'
        )}
      </span>
      
      {pendingCount > 0 && (
        <Badge variant="secondary" className="text-xs">
          {pendingCount}
        </Badge>
      )}
      
      {isProcessing && (
        <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
      )}
      
      {isOnline && pendingCount > 0 && !isProcessing && (
        <Button
          variant="ghost"
          size="sm"
          onClick={processQueue}
          className="h-6 px-2"
          aria-label="Process pending recordings"
        >
          <Upload className="w-3 h-3" />
        </Button>
      )}
    </div>
  );
};

/**
 * Voice queue management panel
 */
export const VoiceQueuePanel = () => {
  const { pendingCount, isProcessing, getRecordings, clearCompleted, processQueue, isOnline } = useVoiceCache();
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRecordings();
  }, [pendingCount]);

  const loadRecordings = async () => {
    setLoading(true);
    const records = await getRecordings();
    setRecordings(records);
    setLoading(false);
  };

  const formatTimestamp = (ts) => {
    return new Date(ts).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="voice-queue-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-medium">Voice Recordings Queue</h3>
        <div className="flex items-center gap-2">
          {isOnline && pendingCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={processQueue}
              disabled={isProcessing}
            >
              <Upload className="w-4 h-4 mr-2" />
              Process All
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={clearCompleted}
          >
            Clear Completed
          </Button>
        </div>
      </div>

      {recordings.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <CloudOff className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No queued recordings</p>
        </div>
      ) : (
        <div className="space-y-2">
          {recordings.map((record) => (
            <div 
              key={record.id}
              className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-700 rounded-lg"
            >
              <div className={`w-2 h-2 rounded-full ${
                record.status === 'completed' ? 'bg-green-500' :
                record.status === 'failed' ? 'bg-red-500' :
                record.status === 'pending' ? 'bg-yellow-500' :
                'bg-gray-400'
              }`} />
              
              <div className="flex-1">
                <div className="text-sm font-medium">
                  Recording {record.id}
                </div>
                <div className="text-xs text-gray-500">
                  {formatTimestamp(record.timestamp)}
                </div>
              </div>
              
              <Badge variant={
                record.status === 'completed' ? 'default' :
                record.status === 'failed' ? 'destructive' :
                'secondary'
              }>
                {record.status}
              </Badge>
              
              {record.status === 'completed' && record.transcript && (
                <span className="text-xs text-gray-500 max-w-[200px] truncate">
                  {record.transcript.substring(0, 50)}...
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VoiceCacheProvider;
